import { useState } from "react";

import { NotificationExpandToggle } from "./NotificationExpandToggle";
import { Column, Row, Text } from "@/components";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";
import { DashboardTaskOverdueBadge } from "@/components/dashboard/seven-day-schedule/DashboardTaskOverdueBadge";

import { agenticScoreToBucket } from "@/lib/agenticScore";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_NOTIFICATION_SURFACES,
  MIXPANEL_SCREENS,
} from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { NotificationCommsToSendPayload, NotificationType } from "@/types";

export const NotificationCommsToSendList = ({
  payload,
  trackingSurface,
}: {
  payload: NotificationCommsToSendPayload;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { candidacies } = payload;

  // Overdue by less than a full day doesn't get a badge.
  const isOverdue = (nextActionAt: string): boolean =>
    dates.getStartOfLocalDay(dates.taskDueDate(nextActionAt)).getTime() <
    dates.getStartOfLocalDay(new Date()).getTime();

  const render = () => (
    <Column className="gap-0.5">
      <NotificationExpandToggle
        isExpanded={isExpanded}
        notificationType={NotificationType.COMMS_TO_SEND}
        setIsExpanded={setIsExpanded}
        trackingSurface={trackingSurface}
      >
        {candidacies.length}{" "}
        {candidacies.length === 1 ? "candidate" : "candidates"}
      </NotificationExpandToggle>
      {isExpanded ? (
        <Column className="gap-1 border-l border-slate-200 pl-3 pt-1">
          {candidacies.map((candidacy) => {
            const bucket = agenticScoreToBucket(candidacy.candidateRating);
            return (
              <Column
                key={candidacy.candidacyId}
                className="cursor-pointer gap-0 rounded-md px-2 py-1.5 hover:bg-slate-50"
                onClick={(e) => {
                  e.stopPropagation();
                  mixpanelUtils.trackRowClick(
                    "Notification list item",
                    MIXPANEL_SCREENS.NOTIFICATIONS,
                    {
                      notification_type: NotificationType.COMMS_TO_SEND,
                      surface: trackingSurface,
                    },
                  );
                  window.open(getCandidacyLink(candidacy), "_blank");
                }}
              >
                <Row className="items-center gap-1.5">
                  {bucket ? (
                    <AgenticScoreChip
                      bucket={bucket}
                      className="gap-0.5 px-1.5 py-0 text-[10px]"
                    />
                  ) : null}
                  <Text className="text-sm">{candidacy.candidateName}</Text>
                  {candidacy.nextActionAt !== null &&
                  isOverdue(candidacy.nextActionAt) ? (
                    <DashboardTaskOverdueBadge
                      dueDate={candidacy.nextActionAt}
                      compact
                    />
                  ) : null}
                </Row>
                <Text className="text-xs font-normal text-slate-500">
                  {candidacy.reqTitle} at {candidacy.customerName}
                </Text>
              </Column>
            );
          })}
        </Column>
      ) : null}
    </Column>
  );

  return render();
};
